import { HttpService, isAxiosError } from "./http";

export interface TrackerTemplate {
  id?: number;
  name: string;
  type: string; // Food, Sleep, Exercise, etc.
  description?: string;
  scale_min?: number;
  scale_max?: number;
  unit?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TrackerTemplateItem {
  id?: number;
  name: string;
  template_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface TrackerEntry {
  id?: number;
  client_id?: number;
  clinic_id?: number;
  tracker_id?: number;
  tracker_template_id?: number;
  entry_datetime: string;
  value: number;
  notes?: string;
  read_by_practitioner?: boolean;
  practitioner_comment?: string;
  created_at?: string;
  updated_at?: string;
  tracker_template?: TrackerTemplate;
  tracker_template_item?: TrackerTemplateItem;
}

export interface HealthTracker {
  id?: number;
  client_id?: number;
  clinic_id?: number;
  tracker_template_id: number;
  is_active: boolean;
  start_date: string;
  end_date?: string;
  frequency: string; // daily, weekly, etc.
  created_at?: string;
  updated_at?: string;
  tracker_template?: TrackerTemplate;
  entries?: TrackerEntry[];
  upcoming_entries?: any[];
}

class HealthTrackerService extends HttpService {
  public constructor() {
    super("/client-app/health-suite/trackers");
  }

  public index(): Promise<HealthTracker[]> {
    return this.http
      .get(this.route)
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }

  public show(trackerId: number): Promise<HealthTracker> {
    return this.http
      .get(`${this.route}/${trackerId}`)
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }

  public getActiveTracker(): Promise<HealthTracker | null> {
    return this.http
      .get(`${this.route}/active`)
      .then(response => {
        const data = this.handleResponse.bind(this)(response);
        
        // Transform the API response to match our interface
        if (data) {
          return {
            ...data,
            is_active: data.status === 'live', // Convert status to is_active boolean
            tracker_template: data.template, // Map template to tracker_template
          };
        }
        
        return data;
      })
      .catch(this.handleError.bind(this));
  }

  public getTrackerEntries(trackerId: number): Promise<any> {
    return this.http
      .get(`${this.route}/${trackerId}/entries`)
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }

  public createEntry(trackerId: number, entry: Omit<TrackerEntry, 'id' | 'read_by_practitioner'>): Promise<TrackerEntry> {
    return new Promise((resolve, reject) => {
      return this.http
        .post(`${this.route}/${trackerId}/entries`, entry)
        .then((response) => {
          this.handleResponse.bind(this);
          resolve(response.data);
        })
        .catch((error) => {
          if (isAxiosError(error) && error.response?.status !== 422) {
            this.handleError.bind(this);
          }
          reject(error);
        });
    });
  }

  public updateEntry(trackerId: number, entryId: number, entry: Partial<TrackerEntry>): Promise<TrackerEntry> {
    return this.http
      .put(`${this.route}/${trackerId}/entries/${entryId}`, entry)
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }

  public getUpcomingEntries(trackerId?: number): Promise<any[]> {
    const params = trackerId ? { tracker_id: trackerId } : {};
    return this.http
      .get(`${this.route}/upcoming`, { params })
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }

  public getLatestEntries(trackerId?: number): Promise<TrackerEntry[]> {
    const params = trackerId ? { tracker_id: trackerId } : {};
    return this.http
      .get(`${this.route}/latest`, { params })
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }

  public getTrackerEntriesByDate(trackerId: number, date: string): Promise<TrackerEntry[]> {
    return this.http
      .get(`${this.route}/${trackerId}/entries/date/${date}`)
      .then(this.handleResponse.bind(this))
      .catch(this.handleError.bind(this));
  }
}

export const healthTrackerService = new HealthTrackerService(); 